import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  ChevronRight,
  Home,
  Loader2,
  LogOut,
  TrendingUp,
} from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';
import { getScores, type ScoreRow, type ScoresResponse } from '../lib/scores-api';
import { AuthService } from '../lib/auth';
import { isExperimentDirectEntryUser, startFreshExperimentDirectInterview } from '../lib/experiment-direct-entry';
import type { UserProfile } from '../types';

const COLORS = {
  bg: '#FAFAF7',
  surface: '#FFFFFF',
  ink: '#18181B',
  muted: '#71717A',
  line: '#E4E4E7',
  strip: '#FAF7EF',
  cyan: '#0E7490',
  cyanSoft: '#ECFEFF',
  orangeFrom: '#FF8A3D',
  orangeTo: '#FF6B1A',
  green: '#15803D',
  greenSoft: '#DCFCE7',
  amber: '#B45309',
  amberSoft: '#FEF3C7',
  rose: '#BE123C',
  roseSoft: '#FFF1F2',
};

const FONT_FAMILY = '"Hiragino Sans", "Noto Sans JP", "Yu Gothic", "Meiryo", system-ui, sans-serif';

function formatDate(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatDateTime(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function combinedScore(row: ScoreRow): number | null {
  if (typeof row.overall_score_value === 'number' && Number.isFinite(row.overall_score_value)) {
    return row.overall_score_value;
  }

  const values = [row.patient_score_value, row.dentist_score_value].filter(
    (value): value is number => typeof value === 'number' && Number.isFinite(value)
  );
  if (values.length === 0) return null;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

type RankMeta = { rank: 'A' | 'B' | 'C' | 'D' | null; sub: string; color: string; soft: string; dots: string };

function scoreTone(score: number | null): RankMeta {
  if (score == null) return { rank: null, sub: '-', color: COLORS.muted, soft: COLORS.strip, dots: '○○○' };
  if (score >= 80) return { rank: 'A', sub: '優秀', color: COLORS.green, soft: COLORS.greenSoft, dots: '●●●' };
  if (score >= 60) return { rank: 'B', sub: '良好', color: COLORS.cyan, soft: COLORS.cyanSoft, dots: '●●○' };
  if (score >= 40) return { rank: 'C', sub: 'あと一歩', color: COLORS.amber, soft: COLORS.amberSoft, dots: '●○○' };
  return { rank: 'D', sub: '要再挑戦', color: COLORS.rose, soft: COLORS.roseSoft, dots: '○○○' };
}

function MetricCard({
  label,
  value,
  unit,
  icon,
}: {
  label: string;
  value: string | number;
  unit?: string;
  icon: ReactNode;
}) {
  return (
    <div
      style={{
        background: COLORS.surface,
        border: `1px solid ${COLORS.line}`,
        borderRadius: 12,
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.15em', color: COLORS.muted }}>
            {label}
          </div>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontSize: 30, fontWeight: 800, lineHeight: 1, color: COLORS.ink }}>
              {value}
            </span>
            {unit && (
              <span style={{ fontSize: 14, fontWeight: 800, color: COLORS.muted }}>{unit}</span>
            )}
          </div>
        </div>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            display: 'grid',
            placeItems: 'center',
            background: COLORS.cyanSoft,
            color: COLORS.cyan,
          }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function ScoreValue({ score }: { score: number | null }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 2,
        fontVariantNumeric: 'tabular-nums',
        color: COLORS.ink,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          minWidth: 42,
          textAlign: 'right',
          fontSize: 22,
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: -0.5,
        }}
      >
        {score == null ? '-' : score}
      </span>
      <span style={{ fontSize: 12, fontWeight: 800, color: COLORS.muted }}>点</span>
    </span>
  );
}

function RankPill({ score }: { score: number | null }) {
  const tone = scoreTone(score);
  return (
    <span
      style={{
        padding: '7px 10px',
        borderRadius: 8,
        background: tone.soft,
        color: tone.color,
        border: `1px solid ${tone.color}`,
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {tone.rank ? `Rank ${tone.rank}` : 'Rank -'}
    </span>
  );
}

function ScoreRowItem({ row }: { row: ScoreRow }) {
  const [, setLocation] = useLocation();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pressed, setPressed] = useState(false);
  const score = combinedScore(row);
  const goToEvaluation = () => setLocation(`/evaluation?interview_id=${row.interview_id}`);
  const active = hovered || focused;
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goToEvaluation}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          goToEvaluation();
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(132px, 1.2fr) minmax(118px, auto) 18px',
        alignItems: 'center',
        gap: 12,
        padding: '14px 10px',
        borderBottom: `1px solid ${COLORS.line}`,
        borderRadius: 8,
        cursor: 'pointer',
        background: active ? COLORS.bg : 'transparent',
        // フォーカス時のみ可視リング（キーボード/スイッチ操作で位置が分かる）
        boxShadow: focused ? `0 0 0 2px ${COLORS.cyan}` : 'none',
        // 押下フィードバック
        transform: pressed ? 'scale(0.99)' : 'none',
        transition: 'background 120ms ease, transform 80ms ease, box-shadow 120ms ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setPressed(false);
      }}
      aria-label={`${formatDateTime(row.created)} の評価を見る`}
    >
      <div
        style={{
          minWidth: 0,
          fontSize: 13,
          fontWeight: 700,
          color: COLORS.ink,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          lineHeight: 1.35,
        }}
      >
        {formatDateTime(row.created)}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 8,
          minWidth: 0,
          justifySelf: 'end',
        }}
      >
        <ScoreValue score={score} />
        <RankPill score={score} />
      </div>
      {/* 末尾の disclosure 矢印: 静止状態でも「タップで遷移」と分かる手がかり */}
      <ChevronRight
        size={18}
        aria-hidden
        style={{
          justifySelf: 'end',
          color: active ? COLORS.cyan : COLORS.muted,
          transform: active ? 'translateX(2px)' : 'none',
          transition: 'color 120ms ease, transform 120ms ease',
        }}
      />
    </div>
  );
}

interface MyPageProps {
  viewedStudent?: {
    id: number;
    name: string;
    participantCode: string | null;
  };
}

export default function MyPage({ viewedStudent }: MyPageProps = {}) {
  const [location, setLocation] = useLocation();
  const authService = AuthService.getInstance();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const isTeacherView = viewedStudent != null;
  const scoreQuery = viewedStudent ? { user_id: viewedStudent.id } : {};

  const { data, isLoading, error } = useQuery<ScoresResponse>({
    queryKey: ['mypage', 'scores', viewedStudent?.id ?? 'self', page],
    queryFn: () => getScores({ ...scoreQuery, page, limit: PAGE_SIZE }),
    staleTime: 60 * 1000,
  });

  const { data: latestData } = useQuery<ScoresResponse>({
    queryKey: ['mypage', 'scores', viewedStudent?.id ?? 'self', 1],
    queryFn: () => getScores({ ...scoreQuery, page: 1, limit: PAGE_SIZE }),
    staleTime: 60 * 1000,
  });

  const { data: currentUser } = useQuery<UserProfile | null>({
    queryKey: ['currentUser'],
    queryFn: () => authService.getCurrentUser(),
    staleTime: 5 * 60 * 1000,
  });
  const isDirectEntryUser = !isTeacherView && isExperimentDirectEntryUser(currentUser);

  const retryButtonLabel = 'もう一度面接する';

  const retryFromInterviewMutation = useMutation({
    mutationFn: async (interviewId: number) => {
      const scoreRes = await fetch(`/api/score/by-interview/${interviewId}`, { credentials: 'include' });
      if (!scoreRes.ok) throw new Error('スコア情報の取得に失敗しました');
      const scoreData = await scoreRes.json();
      const meta = scoreData?.interviewMeta as {
        conditionInstructionId?: number; avatarId?: number;
      } | null;
      if (!meta?.conditionInstructionId) {
        throw new Error('再挑戦に必要な情報が見つかりませんでした');
      }
      const body: Record<string, unknown> = { instructionId: meta.conditionInstructionId, restart: true };
      if (meta.avatarId) body.avatarId = meta.avatarId;
      const startRes = await fetch('/api/start-interview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!startRes.ok) throw new Error(await startRes.text());
      return startRes.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/avatar/current'] });
      setLocation('/interview');
    },
  });

  const directEntryMutation = useMutation({
    mutationFn: async () => {
      if (!currentUser?.experimentEntryTarget) {
        throw new Error('実証実験の直接面接情報が見つかりません');
      }
      return startFreshExperimentDirectInterview(currentUser.experimentEntryTarget);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/avatar/current'] });
      setLocation('/interview');
    },
  });

  const handleTopClick = () => {
    if (isTeacherView) {
      setLocation('/teacher/students');
      return;
    }
    if (isDirectEntryUser) {
      directEntryMutation.mutate();
      return;
    }
    setLocation('/mypage/learning');
  };

  const rows = data?.scores ?? [];
  const latest = latestData?.scores?.[0] ?? null;
  const latestScore = latest ? combinedScore(latest) : null;
  const averageScore = latestData?.statistics?.avg ?? data?.statistics?.avg;
  const totalCount = latestData?.pagination?.total ?? data?.pagination?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);

  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (hash !== '#interview-results') return;
    if (isLoading) return;
    requestAnimationFrame(() => {
      document.getElementById('interview-results')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, [isLoading, rows.length, location]);

  const handleLogout = async () => {
    await authService.logout();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: COLORS.bg,
        color: COLORS.ink,
        fontFamily: FONT_FAMILY,
      }}
    >
      <main style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px 40px' }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', color: COLORS.muted }}>
            {isTeacherView ? 'STUDENT PAGE' : 'MY PAGE'}
          </div>
          <div style={{ marginTop: 4, fontSize: 26, fontWeight: 900, lineHeight: 1.15, letterSpacing: -0.5 }}>
            {isTeacherView ? `${viewedStudent.name}さんの学習履歴` : 'マイページ'}
          </div>
          {isTeacherView && viewedStudent.participantCode && (
            <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: COLORS.muted }}>
              学籍番号 {viewedStudent.participantCode}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 12 }}>
            <button
              type="button"
              onClick={handleTopClick}
              style={{
                flex: 1,
                height: 48,
                padding: '0 24px',
                borderRadius: 999,
                border: 'none',
                background: `linear-gradient(135deg, ${COLORS.orangeFrom} 0%, ${COLORS.orangeTo} 100%)`,
                color: '#FFFFFF',
                boxShadow:
                  '0 0 0 4px rgba(255,138,61,0.18), 0 8px 22px rgba(255,107,26,0.5), 0 2px 6px rgba(0,0,0,0.18)',
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: 0.3,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                whiteSpace: 'nowrap',
              }}
            >
              {isTeacherView ? <ArrowLeft size={18} /> : <Home size={18} />}
              {isTeacherView ? '一覧へ戻る' : isDirectEntryUser ? '面接を始める' : '学習を始める'}
            </button>
            {!isTeacherView && (
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  flexShrink: 0,
                  height: 48,
                  padding: '0 20px',
                  borderRadius: 999,
                  border: `1px solid ${COLORS.line}`,
                  background: COLORS.surface,
                  color: COLORS.ink,
                  fontSize: 14,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <LogOut size={15} />
                ログアウト
              </button>
            )}
          </div>
        </div>

        <section
          style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 16,
            padding: 20,
            marginBottom: 14,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 800, color: COLORS.ink, marginBottom: 14 }}>
            最近の結果
          </div>

          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: COLORS.muted }}>
              <Loader2 size={18} className="animate-spin" />
              読み込み中...
            </div>
          ) : error ? (
            <div style={{ color: COLORS.rose, fontSize: 13, fontWeight: 700 }}>
              面接結果を読み込めませんでした
            </div>
          ) : latest ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '12px 16px',
                  borderRadius: 10,
                  background: '#EFF6FF',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0284C7', letterSpacing: 0.2, fontVariantNumeric: 'tabular-nums' }}>
                  {formatDate(latest.created)}
                </span>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                  <span style={{ fontSize: 36, fontWeight: 900, lineHeight: 1, letterSpacing: -1, color: '#0284C7', fontVariantNumeric: 'tabular-nums' }}>
                    {latestScore ?? '-'}
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: '#0284C7' }}>点</span>
                </span>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => setLocation(`/evaluation?interview_id=${latest.interview_id}`)}
                  style={{
                    flex: 1,
                    height: 44,
                    borderRadius: 10,
                    border: '2px solid #7DD3FC',
                    background: COLORS.surface,
                    color: '#0284C7',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  評価を見る
                </button>
                {!isDirectEntryUser && !isTeacherView && (
                  <button
                    type="button"
                    onClick={() => retryFromInterviewMutation.mutate(latest.interview_id)}
                    disabled={retryFromInterviewMutation.isPending}
                    style={{
                      flex: 1,
                      height: 44,
                      borderRadius: 10,
                      border: '2px solid #7DD3FC',
                      background: COLORS.surface,
                      color: '#0284C7',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: retryFromInterviewMutation.isPending ? 'wait' : 'pointer',
                      opacity: retryFromInterviewMutation.isPending ? 0.5 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    {retryFromInterviewMutation.isPending ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        開始中...
                      </>
                    ) : (
                      retryButtonLabel
                    )}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: COLORS.muted, lineHeight: 1.7 }}>
              {isTeacherView
                ? 'この学生には、まだ面接結果がありません。'
                : 'まだ面接結果はありません。TOPへ戻って面接を開始してください。'}
            </div>
          )}
        </section>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <MetricCard
            label="面接結果"
            value={totalCount}
            unit="件"
            icon={<Calendar size={18} />}
          />
          <MetricCard
            label="平均スコア"
            value={typeof averageScore === 'number' && Number.isFinite(averageScore) ? averageScore.toFixed(1) : '-'}
            unit="点"
            icon={<TrendingUp size={18} />}
          />
        </div>

        <section
          id="interview-results"
          style={{
            scrollMarginTop: 92,
            background: COLORS.surface,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 12,
            padding: '16px 16px 4px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.15em', color: COLORS.muted }}>
                INTERVIEW RESULTS
              </div>
              <h2 style={{ margin: '6px 0 0', fontSize: 18, fontWeight: 800 }}>面接結果一覧</h2>
            </div>
          </div>

          {rows.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                margin: '8px 0 2px',
                fontSize: 12,
                fontWeight: 600,
                color: COLORS.muted,
              }}
            >
              <ChevronRight size={13} aria-hidden />
              {isMobile ? '各行をタップすると、その回の評価を見られます' : '各行をクリックすると、その回の評価を見られます'}
            </div>
          )}

          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '20px 0', color: COLORS.muted }}>
              <Loader2 size={18} className="animate-spin" />
              読み込み中...
            </div>
          ) : error ? (
            <div style={{ padding: '20px 0', color: COLORS.rose, fontSize: 13, fontWeight: 700 }}>
              面接結果を読み込めませんでした
            </div>
          ) : rows.length > 0 ? (
            <>
              <div>
                {rows.map((row) => (
                  <ScoreRowItem key={`${row.interview_id}-${row.created}`} row={row} />
                ))}
              </div>
              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '14px 0 12px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.muted, fontVariantNumeric: 'tabular-nums' }}>
                    全 {totalCount} 件中 {rangeStart} - {rangeEnd} 件を表示
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{
                        height: 36,
                        padding: '0 14px',
                        borderRadius: 8,
                        border: `1px solid ${COLORS.line}`,
                        background: COLORS.surface,
                        color: page === 1 ? COLORS.muted : COLORS.ink,
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: page === 1 ? 'not-allowed' : 'pointer',
                        opacity: page === 1 ? 0.5 : 1,
                      }}
                    >
                      前へ
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                      style={{
                        height: 36,
                        padding: '0 14px',
                        borderRadius: 8,
                        border: `1px solid ${COLORS.line}`,
                        background: COLORS.surface,
                        color: page >= totalPages ? COLORS.muted : COLORS.ink,
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                        opacity: page >= totalPages ? 0.5 : 1,
                      }}
                    >
                      次へ
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: '20px 0', color: COLORS.muted, fontSize: 13, lineHeight: 1.7 }}>
              表示できる面接結果はありません。
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
